import React, { useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../../services/hrmsService";
import PermissionCoverage, { countHolders } from "../../../components/PermissionCoverage";
import { FormDrawer } from "../hrForm";
import { EmptyState, LoadState, PrimaryButton, Tabs } from "../hrComponents";
import { cardStyle, iconBtnStyle, statusBadge, statusLabel, tdStyle, thStyle, useLoad } from "../hrUi";

// Who can do what in HR (permission sets), and the extra fields the company tracks for each employee.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

const PERMISSIONS = [
  { label: "HR Admin", description: "Employees, leave, attendance, roster, documents (not payroll)", value: "HR_ADMIN" },
  { label: "HR Viewer", description: "See everyone, change nothing", value: "HR_VIEWER" },
  { label: "Payroll Admin", description: "Statutory and bank details", value: "PAYROLL_ADMIN" },
  { label: "Finance Approver", description: "Approves payroll runs", value: "FINANCE_APPROVER" },
  { label: "Attendance Admin", description: "Mark attendance, roster, device logs (not payroll or employees)", value: "ATTENDANCE_ADMIN" },
];

function Permissions() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listGrants(), []);
  const [adding, setAdding] = useState(false);
  const rows = data || [];
  const revoke = async (g) => {
    const res = await hrmsService.revokePermission(g.id).catch((e) => ({ success: false, message: e.message }));
    if (res?.success) {
      toast.success(res.message || "Removed");
      reload();
    } else toast.error(hrmsService.responseMessage(res));
  };
  return (
    <>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>The account owner and admins always have every permission. Managers see their own team without any permission.</div>
      {!loading && !error ? <PermissionCoverage permissions={PERMISSIONS} holders={countHolders(rows)} /> : null}
      <div className="mb-3">
        <PrimaryButton onClick={() => setAdding(true)}>Give a permission</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No permissions given" />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Employee", "Permission", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => (
                  <tr key={g.id}>
                    <td style={tdStyle}>
                      {g.full_name} <span style={{ color: "#6B7280", fontSize: 12 }}>{g.employee_code}</span>
                    </td>
                    <td style={tdStyle}>{statusLabel(g.permission)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => revoke(g)}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={adding}
        title="Give a permission"
        onHide={() => setAdding(false)}
        onSave={(clean) => hrmsService.grantPermission(clean.employee_id, clean.permission)}
        onDone={reload}
        fields={[
          { key: "employee_id", label: "Employee", type: "employee", required: true },
          { key: "permission", label: "Permission", type: "select", required: true, options: PERMISSIONS },
        ]}
      />
    </>
  );
}

const FIELD_TYPES = [
  { label: "Text", value: "TEXT" },
  { label: "Number", value: "NUMBER" },
  { label: "Date", value: "DATE" },
  { label: "Dropdown", value: "DROPDOWN" },
];

const toDef = (clean, raw, creating) => {
  const options = String(raw.options_text || "")
    .split("\n")
    .map((o) => o.trim())
    .filter(Boolean);
  return {
    ...(creating ? { field_key: clean.field_key, field_type: clean.field_type } : {}),
    label: clean.label,
    is_required: !!raw.is_required,
    sort_no: clean.sort_no === undefined ? undefined : Number(clean.sort_no),
    ...(raw.field_type === "DROPDOWN" ? { options } : {}),
  };
};

function CustomFields() {
  const { data, loading, error, reload } = useLoad(() => hrmsService.listCustomFields(true), []);
  const [edit, setEdit] = useState(undefined);
  const rows = data || [];
  return (
    <>
      <div className="mb-3">
        <PrimaryButton onClick={() => setEdit(null)}>Add field</PrimaryButton>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {rows.length === 0 ? (
          <EmptyState title="No additional fields" text="Add fields such as blood group or uniform size to every employee’s record." />
        ) : (
          <div style={{ ...cardStyle, overflowX: "auto" }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  {["Label", "Key", "Type", "Required", "Status", ""].map((h) => (
                    <th key={h} style={thStyle}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((f) => (
                  <tr key={f.id}>
                    <td style={tdStyle}>{f.label}</td>
                    <td style={tdStyle}>{f.field_key}</td>
                    <td style={tdStyle}>{statusLabel(f.field_type)}</td>
                    <td style={tdStyle}>{f.is_required ? "Yes" : "—"}</td>
                    <td style={tdStyle}>
                      <span style={statusBadge(f.status || "ACTIVE")}>{statusLabel(f.status || "ACTIVE")}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      <button type="button" style={linkBtn} onClick={() => setEdit(f)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </LoadState>
      <FormDrawer
        show={edit !== undefined}
        title={edit ? "Edit field" : "Add field"}
        initial={edit ? { ...edit, options_text: (Array.isArray(edit.options) ? edit.options : []).join("\n") } : { field_type: "TEXT" }}
        onHide={() => setEdit(undefined)}
        onSave={(clean, raw) => hrmsService.saveCustomField(edit?.id, toDef(clean, raw, !edit))}
        onDone={reload}
        fields={[
          { key: "label", label: "Label", required: true, max: 100 },
          { key: "field_key", label: "Key", required: true, max: 40, disabled: !!edit, hint: "Lower-case letters, digits and underscores, e.g. blood_group" },
          { key: "field_type", label: "Type", type: "select", options: FIELD_TYPES, disabled: !!edit },
          { key: "options_text", label: "Dropdown options (one per line)", type: "textarea", rows: 4 },
          { key: "is_required", label: "Required", type: "checkbox" },
          { key: "sort_no", label: "Order", type: "number", min: 0 },
        ]}
      />
    </>
  );
}

export default function AccessTab() {
  const [tab, setTab] = useState("permissions");
  return (
    <>
      <Tabs tabs={[{ key: "permissions", label: "Permissions" }, { key: "fields", label: "Additional employee fields" }]} active={tab} onChange={setTab} />
      {tab === "permissions" ? <Permissions /> : <CustomFields />}
    </>
  );
}
